/* eslint-disable import/no-cycle */
import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { useAccountStore } from './accountStore';
import {
    isEnabled, isNotExpired, toArray, toFloat, toIntArray, toInteger, toObject, toPromotionPeriodOrNull, toTrimmedArray,
} from './helpers/featureParsers';
import { useRootStore } from './rootStore';
import type { SupportedCashOutConditions } from '@/interface/IBetSlipCashOut';
import type { IPromotionPeriod } from '@/interface/IPromotionPeriod';
import type { IBetBuilderCombinationDisplayRule, IBetBuilderLegSetting, ISingleMatchBetBuilderCombinationDisplayRule } from '@/interface/betBuilder';
import type { BetTypeGroup, TournamentStage } from '@/interface/enum';
import { AbTestGroup, PlayerType, SiteStyle, SportType } from '@/interface/enum';
import { useApp } from '@/libraries/composables/useApp';
import useState from '@/libraries/composables/useState';
import { isAppVersionGreaterThanOrEqual } from '@/libraries/helpers/appVersionHelper';
import localizationHelper, { Backdoor } from '@/libraries/helpers/localizationHelper';
import { formatTournamentCenterStageStartDateByLeagueConfig } from '@/libraries/helpers/tournamentCenterHelper';
import { getOddsApiUrl, getOddsApiWsUrl } from '@/libraries/url';

function createDefaultMoreMarketPrebuiltDisplayRule(): ISingleMatchBetBuilderCombinationDisplayRule {
    return {
        selectionCount: 2,
        combinationCount: 2,
        oddsRange: [
            { min: 3, max: 5, limit: 2 },
            { min: 0, max: 3 },
            { min: 3, max: null },
        ],
    };
}

export function useFeatureToggleState<T>(initialState: T, parser = (stringValue: string) => stringValue as T): [Ref<T>, (stringValue?: string) => void] {
    const [state, _setState] = useState(initialState);

    const setState = (stringValue?: string) => {
        if (stringValue === undefined) return;

        try {
            const parsed = parser(stringValue);
            _setState(parsed);
        } catch (e) {
            console.error('Failed to parse feature toggle state', stringValue, e);
        }
    };

    return [state, setState];
}

export const useFeatureToggleStore = defineStore('featureToggle', () => {
    const [isCasinoWidgetEnabled, setIsCasinoWidgetEnabled] = useFeatureToggleState(false, isEnabled);
    const [isCashOutEnabled, setIsCashOutEnabled] = useFeatureToggleState(false, isEnabled);
    const [isMpCashOutEnabled, setIsMpCashOutEnabled] = useFeatureToggleState(false, isEnabled);
    const [isCashOutPageEnabled, setIsCashOutPageEnabled] = useFeatureToggleState(false, isEnabled);
    const [supportedCashOutConditions, setSupportedCashOutConditions] = useFeatureToggleState<SupportedCashOutConditions>({}, toObject);
    const [cashOutSubBetMinCount, setCashOutSubBetMinCount] = useFeatureToggleState(0, toInteger);
    const [isCashedOutSessionStorageEnabled, setIsCashedOutSessionStorageEnabled] = useFeatureToggleState(false, isEnabled);
    const [isIndiaDefaultCricketEnable, setIsIndiaDefaultCricketEnable] = useFeatureToggleState(false, isEnabled);
    const [isShowLiveInToday, setIsShowLiveInToday] = useFeatureToggleState(false, isEnabled);
    const [isVsSportSwitchEnabled, setIsVsSportSwitchEnabled] = useFeatureToggleState(false, isEnabled);
    const [isFunkyWidgetEnabled, setIsFunkyWidgetEnabled] = useFeatureToggleState(false, isEnabled);
    const [isMiniGameWidgetEnabled, setIsMiniGameWidgetEnabled] = useFeatureToggleState(false, isEnabled);
    const [isNewWidgetSwitchEnabled, setIsNewWidgetSwitchEnabled] = useFeatureToggleState(false, isEnabled);
    const [isGamesWidgetEnabled, setIsGamesWidgetEnabled] = useFeatureToggleState(false, isEnabled);
    const [isShowNewCricketMatchResultEnabled, setIsShowNewCricketMatchResultEnabled] = useFeatureToggleState(false, isEnabled);
    const [inJoiVersion, setInJoiVersion] = useFeatureToggleState('');
    const [isShowInjoiDefaultWidgetEnabled, setIsShowInjoiDefaultWidgetEnabled] = useFeatureToggleState(false, isEnabled);
    const [isUseTokenPrefix, setIsUseTokenPrefix] = useFeatureToggleState(false, isEnabled);
    const [isReverseDisplayOddsEnabled, setIsReverseDisplayOddsEnabled] = useFeatureToggleState(false, isEnabled);
    const [autoRefetchFeatureToggleSeconds, setAutoRefetchFeatureToggleSeconds] = useFeatureToggleState(0, toInteger);
    const [isGLiveStreamEnabled, setIsGLiveStreamEnabled] = useFeatureToggleState(false, isEnabled);
    const [gLiveStreamDomainBlacklist, setGLiveStreamDomainBlacklist] = useFeatureToggleState([], toArray);
    const [gLiveStreamLeagueIdBlacklist, setGLiveStreamLeagueIdBlacklist] = useFeatureToggleState([], toArray);
    const [gLiveStreamRestrictions, setGLiveStreamRestrictions] = useFeatureToggleState([], value => toArray(value, '|'));
    const [isInBetRadarLiveStreamWhiteList, setIsInBetRadarLiveStreamWhiteList] = useFeatureToggleState(false, isEnabled);
    const [isVoucherEnabled, setIsVoucherEnabled] = useFeatureToggleState(false, isEnabled);
    const [isSportsCupZoneEnabled, setIsSportsCupZoneEnabled] = useFeatureToggleState(false, isNotExpired);
    const [simulationSportsZoneLeagueIds, setSimulationSportsZoneLeagueIds] = useFeatureToggleState<Partial<Record<SportType, number[]>>>({}, toObject);
    const [esportsNotAllowCurrencies, setEsportsNotAllowCurrencies] = useFeatureToggleState<string[]>([], toArray);
    const [isMultipleSportsZoneEnabled, setIsMultipleSportsZoneEnabled] = useFeatureToggleState(false, isNotExpired);
    const [eFootballZoneLeagueIds, setEFootballZoneLeagueIds] = useFeatureToggleState([], toIntArray);
    const [sportsCupTournamentNames, setSportsCupTournamentNames] = useFeatureToggleState([], toArray);
    const [multipleSportsTournamentNames, setMultipleSportsTournamentNames] = useFeatureToggleState([], toArray);
    const [tournamentCenterLeagueNames, setTournamentCenterLeagueNames] = useFeatureToggleState([], toArray);
    const [isFirstLoadRefreshBetCountEnabled, setIsFirstLoadRefreshBetCountEnabled] = useFeatureToggleState(false, isEnabled);
    const [refreshBetCountMinDuration, setRefreshBetCountMinDuration] = useFeatureToggleState(5, toInteger);
    const [refreshCashOutMinDuration, setRefreshCashOutMinDuration] = useFeatureToggleState(5, toInteger);
    const [liveMoreDefaultOpenLeagueIds, setLiveMoreDefaultOpenLeagueIds] = useFeatureToggleState([], toIntArray);
    const [isShowDepositInTicketEnabled, setIsShowDepositInTicketEnabled] = useFeatureToggleState(false, isEnabled);
    const [isUsSportsLiveScoreEnabled, setIsUsSportsLiveScoreEnabled] = useFeatureToggleState(false, isEnabled);
    const [setGameScoreSportList, setSetGameScoreSportList] = useFeatureToggleState([], toIntArray);
    const [inningSportList, setInningSportList] = useFeatureToggleState([], toIntArray);
    const [periodSportList, setPeriodSportList] = useFeatureToggleState([], toIntArray);
    const [quarterSportList, setQuarterSportList] = useFeatureToggleState([], toIntArray);
    const [liveScoreProviderConfig, setLiveScoreProviderConfig] = useFeatureToggleState<Partial<Record<SportType, number[]>>>({}, toObject);
    const [isRemoveAllOnMiniParlayEnabled, setIsRemoveAllOnMiniParlayEnabled] = useFeatureToggleState(false, isEnabled);
    const [isSurveyToolEnabled, setIsSurveyToolEnabled] = useFeatureToggleState(false, isEnabled);
    const [placeBetDelayTime, setPlaceBetDelayTime] = useFeatureToggleState(0, toInteger);
    const [isLoadFunkyWidgetFromGames, setIsLoadFunkyWidgetFromGames] = useFeatureToggleState(false, isEnabled);
    const [oddsApiUrl, setOddsApiUrl] = useFeatureToggleState<string | null>(null, getOddsApiUrl);
    const [oddsApiWsUrl, setOddsApiWsUrl] = useFeatureToggleState<string | null>(null, getOddsApiWsUrl);
    const [_isFairExchangeEnabled, setIsFairExchangeEnabled] = useFeatureToggleState(false, isEnabled);
    const [sportsWithNewTagConfig, setSportsWithNewTagConfig] = useFeatureToggleState<SportType[]>([], toIntArray);
    const [supportedSportsForFlagBanner, setSupportedSportsForFlagBanner] = useFeatureToggleState<SportType[]>([], toIntArray);
    const [isMpCombinationEnabled, setIsMpCombinationEnabled] = useFeatureToggleState(false, isEnabled);
    const [isPartialCashOutEnabled, setIsPartialCashOutEnabled] = useFeatureToggleState(false, isEnabled);
    const [_isSportsCupIconEnabled, setIsSportsCupIconEnabled] = useFeatureToggleState(false, isEnabled);
    const [newBetTypeLiveScoreSportMarket, setNewBetTypeLiveScoreSportMarket] = useFeatureToggleState<Partial<Record<SportType, BetTypeGroup[]>>>({}, toObject);
    const [newOddsApiUrl, setNewOddsApiUrl] = useFeatureToggleState<string | null>(null, getOddsApiUrl);
    const [newOddsApiWsUrl, setNewOddsApiWsUrl] = useFeatureToggleState<string | null>(null, getOddsApiWsUrl);
    const [isBetBuilderEnabled, setIsBetBuilderEnabled] = useFeatureToggleState(false, isEnabled);
    const [isInBetBuilderSmaBlackList, setIsInBetBuilderSmaBlackList] = useFeatureToggleState(false, isEnabled);
    const [isJacktimeBetBuilderEnabled, setIsJacktimeBetBuilderEnabled] = useFeatureToggleState(false, isEnabled);
    const [_jacktimeBetBuilderSupportedLeagueIds, setJacktimeBetBuilderSupportedLeagueIds] = useFeatureToggleState('');
    const [betBuilderMinLegCount, setBetBuilderMinLegCount] = useFeatureToggleState(0, toInteger);
    const [betBuilderMinLegSettings, setBetBuilderMinLegSettings] = useFeatureToggleState<IBetBuilderLegSetting[]>([], toObject);
    const [isJackpotEnabled, setIsJackpotEnabled] = useFeatureToggleState(false, isEnabled);
    const [isMpTwoSubBetEnabled, setIsMpTwoSubBetEnabled] = useFeatureToggleState(false, isEnabled);
    const [mpTwoSubBetLeagueIds, setMpTwoSubBetLeagueIds] = useFeatureToggleState([], toIntArray);
    const [isBetBuilderTutorialEnabled, setIsBetBuilderTutorialEnabled] = useFeatureToggleState(false, isEnabled);
    const [multipleSportsZoneSupportSports, setMultipleSportsZoneSupportSports] = useFeatureToggleState<SportType[]>([], toIntArray);
    const [exchangeWhiteListByCurrency, setExchangeWhiteListByCurrency] = useFeatureToggleState([], toArray);
    const [exchangeWhiteListByCountry, setExchangeWhiteListByCountry] = useFeatureToggleState([], toArray);
    const [defaultSportType, setDefaultSportType] = useFeatureToggleState<SportType>(SportType.Soccer, toInteger);
    const [isSseEnabled, setIsSseEnabled] = useFeatureToggleState(false, isEnabled);
    const [showDynamicMarketsSportTypes, setShowDynamicMarketsSportTypes] = useFeatureToggleState<SportType[]>([], toIntArray);
    const [isDynamicMarketEnabled, setIsDynamicMarketEnabled] = useFeatureToggleState(false, isEnabled);
    const [isDynamicMarketMTokenEnabled, setIsDynamicMarketMTokenEnabled] = useFeatureToggleState(false, isEnabled);
    const [isNewResultsEnabled, setIsNewResultsEnabled] = useFeatureToggleState(false, isEnabled);
    const [isIdleMonitorEnabled, setIsIdleMonitorEnabled] = useFeatureToggleState(false, isEnabled);
    const [_isSingleModeBetSlipForceEnable, setIsSingleModeBetSlipForceEnable] = useFeatureToggleState(false, isEnabled);
    const [isSingleModeBetSlipTutorialEnabled, setIsSingleModeBetSlipTutorialEnabled] = useFeatureToggleState(false, isEnabled);
    const [isCricketEnabled, setIsCricketEnabled] = useFeatureToggleState(false, isEnabled);
    const [isInEFootballLeagueBlackList, setIsInEFootballLeagueBlackList] = useFeatureToggleState(false, isEnabled);
    const [filterEFootballLeagueIds, setFilterEFootballLeagueIds] = useFeatureToggleState([], toIntArray);
    const [_isTournamentCenterEnabled, setIsTournamentCenterEnabled] = useFeatureToggleState(false, isNotExpired);
    const [tournamentCenterOutrightLeagueId, setTournamentCenterOutrightLeagueId] = useFeatureToggleState(0, toInteger);
    const [tournamentCenterStageStartDateByLeagueConfig, setTournamentCenterStageStartDateByLeagueConfig] = useFeatureToggleState<Map<number, Map<TournamentStage, Date>>>(new Map(), formatTournamentCenterStageStartDateByLeagueConfig);
    const [tournamentCenterMainLeagueIds, setTournamentCenterMainLeagueIds] = useFeatureToggleState([], toIntArray);
    const [tournamentCenterStandingLeagueIds, setTournamentCenterStandingLeagueIds] = useFeatureToggleState([], toIntArray);
    const [tournamentCenterGroupWinnerLeagueName, setTournamentCenterGroupWinnerLeagueName] = useFeatureToggleState('');
    const [isSingleRepeatBetEnabled, setIsSingleRepeatBetEnabled] = useFeatureToggleState(false, isEnabled);
    const [betGeniusCustomerId, setBetGeniusCustomerId] = useFeatureToggleState('');
    const [isTournamentCenterEntranceEnabled, setIsTournamentCenterEntranceEnabled] = useFeatureToggleState(false, isEnabled);
    const [isTournamentCenterSportListEntranceEnabled, setIsTournamentCenterSportListEntranceEnabled] = useFeatureToggleState(false, isEnabled);
    const [isTournamentCenterMainPageEntranceEnabled, setIsTournamentCenterMainPageEntranceEnabled] = useFeatureToggleState(false, isEnabled);
    const [_isPromotionEnabled, setIsPromotionEnabled] = useFeatureToggleState(false, isEnabled);
    const [isPromotionNotExpired, setPromotionExpiredDate] = useFeatureToggleState(false, isNotExpired);
    const [isBetBuilderDisplayPriorityEnabled, setIsBetBuilderDisplayPriorityEnabled] = useFeatureToggleState(false, isEnabled);

    const [isFantasySelectionEnabled, setIsFantasySelectionEnabled] = useFeatureToggleState(false, isEnabled);
    const [ePenaltyLeagueIds, setEPenaltyLeagueIds] = useFeatureToggleState([], toIntArray);
    const [isEPenaltyCardEnabled, setIsEPenaltyCardEnabled] = useFeatureToggleState(false, isEnabled);
    const [isBetBuilderPrebuiltEnabled, setIsBetBuilderPreBuiltEnabled] = useFeatureToggleState(false, isEnabled);
    const [betBuilderCombinationDisplayRule, setBetBuilderCombinationDisplayRule] = useFeatureToggleState<IBetBuilderCombinationDisplayRule[]>([], toObject);
    const [isMoreMarketPrebuiltEnabled, setIsMoreMarketPrebuiltEnabled] = useFeatureToggleState(false, isEnabled);
    const [moreMarketPrebuiltDisplayRule, setMoreMarketPrebuiltDisplayRule] = useFeatureToggleState<ISingleMatchBetBuilderCombinationDisplayRule>(
        createDefaultMoreMarketPrebuiltDisplayRule(),
        toObject,
    );
    const [isPrebuiltInMatchDetailAndBetBuilderTabEnabled, setIsPrebuiltInMatchDetailAndBetBuilderTabEnabled] = useFeatureToggleState(false, isEnabled);
    const [matchDetailPrebuiltDisplayRule, setMatchDetailPrebuiltDisplayRule] = useFeatureToggleState<ISingleMatchBetBuilderCombinationDisplayRule>(
        createDefaultMoreMarketPrebuiltDisplayRule(),
        toObject,
    );
    const [isTelegramSportsbookBotEntranceEnabled, setIsTelegramSportsbookBotEntranceEnabled] = useFeatureToggleState(false, isEnabled);
    const [_isEFootballInTournamentCenterEnabled, setIsEFootballInTournamentCenterEnabled] = useFeatureToggleState(false, isEnabled);
    const [isJackpotEFootballInBigEventTheme, setIsJackpotEFootballInBigEventTheme] = useFeatureToggleState(false, isEnabled);

    const [isApmEnabled, setIsApmEnabled] = useFeatureToggleState(false, isEnabled);
    const [apmSampleRate, setApmSampleRate] = useFeatureToggleState(0, toFloat);
    const [apmServerUrl, setApmServerUrl] = useFeatureToggleState('');

    const [isMuayThaiOneBxInSportEnabled, setIsMuayThaiOneBxInSportEnabled] = useFeatureToggleState(false, isEnabled);
    const [muayThaiOneBxAllowedPlayerTypes, setMuayThaiOneBxAllowedPlayerTypes] = useFeatureToggleState<PlayerType[]>([], toIntArray);

    const [isStatisticsEnabled, setIsStatisticsEnabled] = useFeatureToggleState(false, isEnabled);

    const [liveStreamPipMinAppVersion, setLiveStreamPipMinAppVersion] = useFeatureToggleState('');

    const [isSwitchEFootballToSimulationSportsZoneEnabled, setIsSwitchEFootballToSimulationSportsZoneEnabled] = useFeatureToggleState(false, isEnabled);

    // Global settings Member_ScorePredictionLeagueNames and Member_ScorePredictionPromotionPeriod (JSON, shared with
    // Desktop TCSP-8392). Pollux GetSiteConfig forwards Member_* to Castor, strips the
    // prefix and camel-cases the key, so they arrive as scorePredictionLeagueNames / scorePredictionPromotionPeriod.
    const [scorePredictionLeagueNames, setScorePredictionLeagueNames] = useFeatureToggleState<string[]>([], toTrimmedArray);
    const [scorePredictionPromotionPeriod, setScorePredictionPromotionPeriod] = useFeatureToggleState<IPromotionPeriod | null>(null, toPromotionPeriodOrNull);

    const rootStore = storeToRefs(useRootStore());
    const accountStore = storeToRefs(useAccountStore());
    const { isCurrencyInList, isIpCountryInList } = useAccountStore();

    const isBsi = computed(() => !rootStore.isAsi.value);
    const isAsi = computed(() => rootStore.isAsi.value);

    const { appVersion } = useApp();
    const isLiveStreamPipEnabled = computed(() => isAppVersionGreaterThanOrEqual(appVersion, liveStreamPipMinAppVersion.value));

    const isIndiaSpecialRule = computed(() => {
        if (isBsi.value) return false;
        return isCurrencyInList(['INR']);
    });

    const backdoor = localizationHelper.getBackdoor();
    const isEuroSite = computed(() => {
        if (backdoor !== Backdoor.None) {
            return true;
        }

        if (isBsi.value) return false;

        return accountStore.customerInfo.value.siteStyle === SiteStyle.Euro;
    });

    const isMiniMpTicketEnabled = computed(() => !isEuroSite.value);

    const isFairExchangeWhiteList = computed(() => {
        if (isAsi.value && accountStore.customerInfo.value.playerType === PlayerType.B2C) {
            return isCurrencyInList(exchangeWhiteListByCurrency.value);
        }

        return isIpCountryInList(exchangeWhiteListByCountry.value);
    });

    const isPromotionEnabled = computed(() => isPromotionNotExpired.value && _isPromotionEnabled.value);

    const isFairExchangeEnabled = computed(() => isFairExchangeWhiteList.value && _isFairExchangeEnabled.value);

    const singleModeBetSlipAbTestGroups = [AbTestGroup.SingleModeBetSlipTbCurrency, AbTestGroup.SingleModeBetSlipPpCurrency];
    const isSingleModeBetSlipInBGroup = computed(() => singleModeBetSlipAbTestGroups.some(group => accountStore.customerInfo.value.abTestGroup[group]?.isUseNewFeature ?? false));
    const isSingleModeBetSlipEnabled = computed(() => (_isSingleModeBetSlipForceEnable.value || isSingleModeBetSlipInBGroup.value) && !isEuroSite.value);

    const isSportsCupIconEnabled = computed(() => isSportsCupZoneEnabled.value && _isSportsCupIconEnabled.value);

    const isSupportMpTwoSubBets = (leagueId: number) => {
        if (!isMpTwoSubBetEnabled.value) {
            return false;
        }
        return mpTwoSubBetLeagueIds.value.includes(leagueId);
    };

    const isDynamicMarketsEnabled = (sportType: SportType) => showDynamicMarketsSportTypes.value.includes(sportType) && isDynamicMarketEnabled.value;

    const isTournamentCenterEnabled = computed(() => _isTournamentCenterEnabled.value && !isEuroSite.value);

    function isJacktimeBetBuilderSupportedLeague(leagueId: number) {
        if (_jacktimeBetBuilderSupportedLeagueIds.value === '*') return true;
        return toIntArray(_jacktimeBetBuilderSupportedLeagueIds.value).includes(leagueId);
    }

    const isEFootballInTournamentCenterEnabled = computed(() => _isEFootballInTournamentCenterEnabled.value && !isBsi.value);

    function setFeatureToggle(toggle: Partial<Record<string, string>>) {
        setIsCasinoWidgetEnabled(toggle.isCasinoWidgetEnabled);

        setIsCashOutEnabled(toggle.isCashOutEnabled);
        setIsMpCashOutEnabled(toggle.isMpCashOutEnabled);
        setIsCashOutPageEnabled(toggle.cashOutPageEnabled);
        setSupportedCashOutConditions(toggle.supportedCashOutConditions);

        setCashOutSubBetMinCount(toggle.cashOutSubBetMinCount);
        setIsCashedOutSessionStorageEnabled(toggle.isCashedOutSessionStorageEnabled);

        setIsIndiaDefaultCricketEnable(toggle.isIndiaDefaultCricketEnable);
        setIsShowLiveInToday(toggle.isShowLiveInToday);
        setIsVsSportSwitchEnabled(toggle.isVsSportSwitchEnabled);
        setIsShowNewCricketMatchResultEnabled(toggle.isShowNewCricketMatchResultEnabled);

        setIsFunkyWidgetEnabled(toggle.isFunkyWidgetEnabled);
        setIsMiniGameWidgetEnabled(toggle.isMiniGameWidgetEnabled);
        setIsNewWidgetSwitchEnabled(toggle.isNewWidgetSwitchEnabled);
        setIsGamesWidgetEnabled(toggle.isGamesWidgetEnabled);

        setInJoiVersion(toggle.inJoiVersion);
        setIsShowInjoiDefaultWidgetEnabled(toggle.isShowInjoiDefaultWidgetEnabled);
        setIsUseTokenPrefix(toggle.isUseTokenPrefix);
        window.isUseTokenPrefix = isUseTokenPrefix.value;
        setIsReverseDisplayOddsEnabled(toggle.isReverseDisplayOddsEnabled);

        setAutoRefetchFeatureToggleSeconds(toggle.autoRefetchFeatureToggleSeconds);

        setIsGLiveStreamEnabled(toggle.isGLiveStreamEnabled);
        setGLiveStreamDomainBlacklist(toggle.gLiveStreamDomainBlacklist);
        setGLiveStreamLeagueIdBlacklist(toggle.gLiveStreamLeagueIdBlacklist);
        setGLiveStreamRestrictions(toggle.gLiveStreamRestrictions);
        setIsInBetRadarLiveStreamWhiteList(toggle.isInAvApiWhiteList);

        setIsVoucherEnabled(toggle.isVoucherEnabled);

        setIsSportsCupZoneEnabled(toggle.sportsCupZoneExpiredDate);
        setSimulationSportsZoneLeagueIds(toggle.simulationSportsZoneLeagueIds);
        setEsportsNotAllowCurrencies(toggle.esportsNotAllowCurrencies);
        setIsMultipleSportsZoneEnabled(toggle.multipleSportsZoneExpiredDate);
        setEFootballZoneLeagueIds(toggle.eFootballZoneLeagueIds);
        setSportsCupTournamentNames(toggle.sportsCupTournamentNames);
        setIsFirstLoadRefreshBetCountEnabled(toggle.isFirstLoadRefreshBetCountEnabled);
        setRefreshBetCountMinDuration(toggle.refreshBetCountMinDuration);
        setRefreshCashOutMinDuration(toggle.refreshCashOutMinDuration);
        setMultipleSportsTournamentNames(toggle.multipleSportsTournamentNames);
        setTournamentCenterLeagueNames(toggle.tournamentCenterLeagueNames);

        setLiveMoreDefaultOpenLeagueIds(toggle.liveMoreDefaultOpenLeagueIds);

        setIsShowDepositInTicketEnabled(toggle.isShowDepositInTicketEnabled);

        setIsUsSportsLiveScoreEnabled(toggle.isUsSportsLiveScoreEnabled);
        setPeriodSportList(toggle.periodSportList);
        setQuarterSportList(toggle.quarterSportList);
        setInningSportList(toggle.inningSportList);
        setSetGameScoreSportList(toggle.setGameScoreSportList);
        setLiveScoreProviderConfig(toggle.liveScoreProviderConfig);
        setIsRemoveAllOnMiniParlayEnabled(toggle.isRemoveAllOnMiniParlayEnabled);
        setIsSurveyToolEnabled(toggle.isSurveyToolEnabled);

        setPlaceBetDelayTime(toggle.placeBetDelayTime);

        setIsLoadFunkyWidgetFromGames(toggle.isLoadFunkyWidgetFromGames);

        setOddsApiUrl(toggle.oddsApiUrl);
        setOddsApiWsUrl(toggle.oddsApiWsUrl);

        setNewOddsApiUrl(toggle.newOddsApiUrl);
        setNewOddsApiWsUrl(toggle.newOddsApiWsUrl);

        setIsFairExchangeEnabled(toggle.isFairExchangeEnabled);
        setSportsWithNewTagConfig(toggle.sportsWithNewTagConfig);
        setSupportedSportsForFlagBanner(toggle.supportedSportsForFlagBanner);
        setIsMpCombinationEnabled(toggle.isMpCombinationEnabled);
        setIsPartialCashOutEnabled(toggle.isPartialCashOutEnabled);
        setIsSportsCupIconEnabled(toggle.isSportsCupIconEnabled);
        setNewBetTypeLiveScoreSportMarket(toggle.newBetTypeLiveScoreSportMarket);
        setIsBetBuilderEnabled(toggle.isBetBuilderEnabled);
        setIsInBetBuilderSmaBlackList(toggle.isInBetBuilderSmaBlackList);
        setIsJacktimeBetBuilderEnabled(toggle.isJtBetBuilderEnabled);
        setJacktimeBetBuilderSupportedLeagueIds(toggle.jtBetBuilderSupportedLeagueIds);
        setIsBetBuilderTutorialEnabled(toggle.isBetBuilderTutorialPopupEnabled);
        setBetBuilderMinLegCount(toggle.betBuilderMinLegCount);
        setIsJackpotEnabled(toggle.isJackpotEnabled);
        setIsMpTwoSubBetEnabled(toggle.isTwoMpSubBetEnabled);
        setMpTwoSubBetLeagueIds(toggle.mpTwoSubBetLeagueIds);
        setMultipleSportsZoneSupportSports(toggle.multipleSportsZoneSupportSports);
        setBetBuilderMinLegSettings(toggle.betBuilderMinLegSettings);
        setBetBuilderCombinationDisplayRule(toggle.betBuilderCombinationDisplayRule);
        setExchangeWhiteListByCurrency(toggle.exchangeWhiteListByCurrency);
        setExchangeWhiteListByCountry(toggle.exchangeWhiteListByCountry);
        setDefaultSportType(toggle.defaultSportType);
        setIsSseEnabled(toggle.isSseEnabled);
        setShowDynamicMarketsSportTypes(toggle.showDynamicMarketsSportTypes);
        setIsDynamicMarketEnabled(toggle.isDynamicMarketEnabled);
        setIsDynamicMarketMTokenEnabled(toggle.isDynamicMarketMTokenEnabled);
        setIsNewResultsEnabled(toggle.isNewResultsEnabled);
        setIsIdleMonitorEnabled(toggle.isIdleMonitorEnabled);
        setIsSingleModeBetSlipForceEnable(toggle.isSingleModeBetSlipForceEnable);
        setIsSingleModeBetSlipTutorialEnabled(toggle.isSingleModeBetSlipTutorialEnabled);
        setIsCricketEnabled(toggle.isCricketEnabled);
        setIsInEFootballLeagueBlackList(toggle.isInEFootballLeagueBlackList);
        setFilterEFootballLeagueIds(toggle.filteredEFootballLeagueIds);
        setIsTournamentCenterEnabled(toggle.tournamentCenterExpiredDate);
        setPromotionExpiredDate(toggle.promotionExpiredDate);
        setTournamentCenterOutrightLeagueId(toggle.tournamentCenterOutrightLeagueId);
        setTournamentCenterStageStartDateByLeagueConfig(toggle.tournamentCenterStageStartDateByLeagueConfig);
        setTournamentCenterMainLeagueIds(toggle.tournamentCenterMainLeagueIds);
        setTournamentCenterStandingLeagueIds(toggle.tournamentCenterStandingLeagueIds);
        setTournamentCenterGroupWinnerLeagueName(toggle.tournamentCenterGroupWinnerLeagueName);
        setIsPromotionEnabled(toggle.isPromotionEnabled);
        setIsSingleRepeatBetEnabled(toggle.isSingleRepeatBetEnabled);
        setBetGeniusCustomerId(toggle.betGeniusCustomerId);
        setIsTournamentCenterEntranceEnabled(toggle.isTournamentCenterEntranceEnabled);
        setIsTournamentCenterSportListEntranceEnabled(toggle.isTournamentCenterSportListEntranceEnabled);
        setIsTournamentCenterMainPageEntranceEnabled(toggle.isTournamentCenterMainPageEntranceEnabled);
        setIsFantasySelectionEnabled(toggle.isFantasySelectionEnabled);
        setEPenaltyLeagueIds(toggle.epenaltyLeagueIds);
        setIsEPenaltyCardEnabled(toggle.isEPenaltyCardEnabled);
        setIsBetBuilderPreBuiltEnabled(toggle.isBetBuilderPrebuiltEnabled);
        setIsMoreMarketPrebuiltEnabled(toggle.isMoreMarketPrebuiltEnabled);
        setIsPrebuiltInMatchDetailAndBetBuilderTabEnabled(toggle.isPrebuiltInMatchDetailAndBetBuilderTabEnabled);
        setMatchDetailPrebuiltDisplayRule(toggle.matchDetailPrebuiltDisplayRule);
        setMoreMarketPrebuiltDisplayRule(toggle.moreMarketPrebuiltDisplayRule);
        setIsTelegramSportsbookBotEntranceEnabled(toggle.isTelegramSportsbookBotEntranceEnabled);
        setIsEFootballInTournamentCenterEnabled(toggle.isEFootballInTournamentCenterEnabled);
        setIsJackpotEFootballInBigEventTheme(toggle.isJackpotEFootballInBigEventTheme);
        setIsBetBuilderDisplayPriorityEnabled(toggle.isBetBuilderDisplayPriorityEnabled);

        setIsApmEnabled(toggle.isApmEnabled);
        setApmSampleRate(toggle.apmSampleRate);
        setApmServerUrl(toggle.apmServerUrl);

        setIsMuayThaiOneBxInSportEnabled(toggle.isMuayThaiOneBxInSportEnabled);
        setMuayThaiOneBxAllowedPlayerTypes(toggle.muayThaiOneBxAllowedPlayerTypes);

        setIsStatisticsEnabled(toggle.isStatisticsEnabled);
        setLiveStreamPipMinAppVersion(toggle.liveStreamPipMinAppVersion);
        setIsSwitchEFootballToSimulationSportsZoneEnabled(toggle.isSwitchEFootballToSimulationSportsZoneEnabled);

        setScorePredictionLeagueNames(toggle.scorePredictionLeagueNames);
        setScorePredictionPromotionPeriod(toggle.scorePredictionPromotionPeriod);
    }

    return {
        setFeatureToggle,
        isCasinoWidgetEnabled,
        isCashOutEnabled,
        isMpCashOutEnabled,
        isCashOutPageEnabled,
        supportedCashOutConditions,
        cashOutSubBetMinCount,
        isCashedOutSessionStorageEnabled,
        isIndiaDefaultCricketEnable,
        isShowLiveInToday,
        isVsSportSwitchEnabled,
        isFunkyWidgetEnabled,
        isMiniGameWidgetEnabled,
        isNewWidgetSwitchEnabled,
        isGamesWidgetEnabled,
        isShowNewCricketMatchResultEnabled,
        inJoiVersion,
        isShowInjoiDefaultWidgetEnabled,
        isUseTokenPrefix,
        isReverseDisplayOddsEnabled,
        autoRefetchFeatureToggleSeconds,
        isGLiveStreamEnabled,
        gLiveStreamDomainBlacklist,
        gLiveStreamLeagueIdBlacklist,
        gLiveStreamRestrictions,
        isVoucherEnabled,
        isSportsCupZoneEnabled,
        simulationSportsZoneLeagueIds,
        esportsNotAllowCurrencies,
        isMultipleSportsZoneEnabled,
        isFantasySelectionEnabled,
        eFootballZoneLeagueIds,
        sportsCupTournamentNames,
        multipleSportsTournamentNames,
        tournamentCenterLeagueNames,
        isFirstLoadRefreshBetCountEnabled,
        refreshBetCountMinDuration,
        refreshCashOutMinDuration,
        liveMoreDefaultOpenLeagueIds,
        isShowDepositInTicketEnabled,
        isUsSportsLiveScoreEnabled,
        setGameScoreSportList,
        inningSportList,
        periodSportList,
        quarterSportList,
        liveScoreProviderConfig,
        isRemoveAllOnMiniParlayEnabled,
        isSurveyToolEnabled,
        placeBetDelayTime,
        isLoadFunkyWidgetFromGames,
        oddsApiUrl,
        oddsApiWsUrl,
        isFairExchangeEnabled,
        sportsWithNewTagConfig,
        supportedSportsForFlagBanner,
        isMpCombinationEnabled,
        isIndiaSpecialRule,
        isEuroSite,
        isMiniMpTicketEnabled,
        isPartialCashOutEnabled,
        isSportsCupIconEnabled,
        newBetTypeLiveScoreSportMarket,
        newOddsApiUrl,
        newOddsApiWsUrl,
        isBetBuilderEnabled,
        isInBetBuilderSmaBlackList,
        isJacktimeBetBuilderEnabled,
        isJacktimeBetBuilderSupportedLeague,
        betBuilderMinLegCount,
        isJackpotEnabled,
        isSupportMpTwoSubBets,
        isBetBuilderTutorialEnabled,
        multipleSportsZoneSupportSports,
        betBuilderMinLegSettings,
        isInBetRadarLiveStreamWhiteList,
        defaultSportType,
        isSseEnabled,
        isDynamicMarketsEnabled,
        isDynamicMarketEnabled,
        isDynamicMarketMTokenEnabled,
        isNewResultsEnabled,
        isIdleMonitorEnabled,
        isSingleModeBetSlipEnabled,
        isSingleModeBetSlipTutorialEnabled,
        isCricketEnabled,
        isPromotionEnabled,
        isInEFootballLeagueBlackList,
        filterEFootballLeagueIds,
        isTournamentCenterEnabled,
        isTournamentCenterEntranceEnabled,
        isTournamentCenterSportListEntranceEnabled,
        isTournamentCenterMainPageEntranceEnabled,
        tournamentCenterOutrightLeagueId,
        tournamentCenterStageStartDateByLeagueConfig,
        tournamentCenterMainLeagueIds,
        tournamentCenterStandingLeagueIds,
        tournamentCenterGroupWinnerLeagueName,
        isSingleRepeatBetEnabled,
        betGeniusCustomerId,
        ePenaltyLeagueIds,
        isEPenaltyCardEnabled,
        isBetBuilderPrebuiltEnabled,
        isMoreMarketPrebuiltEnabled,
        isPrebuiltInMatchDetailAndBetBuilderTabEnabled,
        matchDetailPrebuiltDisplayRule,
        moreMarketPrebuiltDisplayRule,
        betBuilderCombinationDisplayRule,
        isTelegramSportsbookBotEntranceEnabled,
        isEFootballInTournamentCenterEnabled,
        isJackpotEFootballInBigEventTheme,
        isApmEnabled,
        apmSampleRate,
        apmServerUrl,
        isMuayThaiOneBxInSportEnabled,
        muayThaiOneBxAllowedPlayerTypes,
        isStatisticsEnabled,
        isLiveStreamPipEnabled,
        isSwitchEFootballToSimulationSportsZoneEnabled,
        isBetBuilderDisplayPriorityEnabled,
        scorePredictionLeagueNames,
        scorePredictionPromotionPeriod,
    };
});
